import React, { useState } from 'react';
import { markMessageAsRead, deleteMessage } from '../../api/messagingApi';
import './MessageItem.css';

/**
 * MessageItem - Individual message card component
 * 
 * @param {Object} props
 * @param {Object} props.message - Message object
 * @param {Function} props.onMessageUpdated - Callback when message is updated
 * @param {Function} props.onMessageDeleted - Callback when message is deleted
 */
function MessageItem({ message, onMessageUpdated, onMessageDeleted }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // VALIDATE PROPS - NO SILENT FAILURES
    if (!message) {
        throw new Error('MessageItem: message prop is required');
    }
    if (!message.id) {
        throw new Error(`MessageItem: message.id is required - got ${JSON.stringify(message)}`);
    }

    const isUnread = message.status === 'UNREAD';
    const isSystemMessage = message.message_type === 'SYSTEM_BROADCAST' || message.message_type === 'SYSTEM_TARGETED';
    const isCannedMessage = message.message_type === 'P2P_CANNED';

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const handleToggleExpand = async () => {
        if (!isExpanded && isUnread) {
            // Mark as read when expanding
            try {
                await markMessageAsRead(message.id);
                if (onMessageUpdated) {
                    onMessageUpdated(message.id, { status: 'READ' });
                }
            } catch (error) {
                console.error('MessageItem: Error marking message as read:', error);
                // Continue to expand even if mark as read fails
            }
        }
        setIsExpanded(!isExpanded);
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this message?')) {
            return;
        }

        setIsDeleting(true);
        try {
            await deleteMessage(message.id);
            if (onMessageDeleted) {
                onMessageDeleted(message.id);
            }
        } catch (error) {
            console.error('MessageItem: Error deleting message:', error);
            alert(error.message || 'Failed to delete message. Please try again.');
            setIsDeleting(false);
        }
    };

    // Get sender display name
    const getSenderName = () => {
        if (isSystemMessage) {
            return 'System';
        }
        return message.from_user?.username || 'Unknown User';
    };

    // Get message preview (first 100 chars of body)
    const getPreview = () => {
        if (!message.body) return '';
        if (message.body.length <= 100) return message.body;
        return message.body.substring(0, 100) + '...';
    };

    return (
        <div className={`message-item ${isUnread ? 'message-item-unread' : ''} ${isSystemMessage ? 'message-item-system' : ''}`}>
            <div className="message-item-header" onClick={handleToggleExpand}>
                <div className="message-item-sender-info">
                    {isUnread && <div className="message-unread-indicator" />}
                    <div className="message-sender-avatar">
                        {isSystemMessage ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                                <path d="M2 17l10 5 10-5"></path>
                                <path d="M2 12l10 5 10-5"></path>
                            </svg>
                        ) : (
                            <img src="/art/UserIcon.png" alt="User" />
                        )}
                    </div>
                    <div className="message-sender-meta">
                        <div className="message-sender-name">
                            {getSenderName()}
                            {isCannedMessage && message.canned_subtype && (
                                <span className="message-type-badge">{message.canned_subtype}</span>
                            )}
                        </div>
                        <div className="message-preview">
                            {message.subject && <span className="message-subject">{message.subject}: </span>}
                            {isExpanded ? message.body : getPreview()}
                        </div>
                    </div>
                </div>
                <div className="message-item-right">
                    <div className="message-timestamp">{formatTimestamp(message.created_at)}</div>
                    <div className="message-expand-icon">
                        {isExpanded ? '▲' : '▼'}
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="message-item-expanded">
                    <div className="message-body-full">
                        {message.subject && <div className="message-subject-full">{message.subject}</div>}
                        <div className="message-text">{message.body}</div>
                    </div>

                    {/* Show battle data if it's a battle result message */}
                    {message.canned_subtype === 'BATTLE_RESULT' && message.battle_data && (
                        <div className="message-battle-info">
                            <div className="battle-info-title">⚔️ Battle Result</div>
                            <div className="battle-info-details">
                                <div className="battle-info-row">
                                    <span className="battle-info-label">League:</span>
                                    <span className="battle-info-value">{message.battle_data.league_name}</span>
                                </div>
                                <div className="battle-info-row">
                                    <span className="battle-info-label">Placement:</span>
                                    <span className="battle-info-value">{message.battle_data.placement}</span>
                                </div>
                                {message.battle_data.score && (
                                    <div className="battle-info-row">
                                        <span className="battle-info-label">Score:</span>
                                        <span className="battle-info-value">{message.battle_data.score}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="message-item-actions">
                        <button
                            className="message-action-button message-delete-button"
                            onClick={handleDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MessageItem;
